<script setup lang="ts">
import LineCreator from '@/components/charts/chart-creator/LineCreator.vue'
import {
  BaseSerializedChart,
  BaseSerializedInfluxLineChart,
  SerializedCameraBasedChart,
} from '@/composables/charts/influx-charts/chart-builders/ChartInterfaces'
import { SerializedGeneralChartTimeSetup } from '@/composables/charts/influx-charts/chart-serialization/ChartTimeSetup'
import { computed, ComputedRef, Ref, ref, watch } from 'vue'
import { CHART_TYPES } from '@/composables/charts/influx-charts/chart-builders/CHART_TYPES'
import CameraTagSelector from '@/components/charts/chart-creator/CameraTagSelector.vue'
import { TagSelector } from '@/composables/charts/influx-charts/query-builders/InfluxTypes'
import _ from 'lodash'
import { InfluxLineChartBuilder } from '@/composables/charts/influx-charts/chart-builders/InfluxLineChartBuilder'
import { useDisplay } from 'vuetify'

const props = defineProps<{ baseSerialized: BaseSerializedChart | null; timeSetup: SerializedGeneralChartTimeSetup | null }>()
const model = defineModel<SerializedCameraBasedChart | null>()

const { mobile } = useDisplay()

const serializedLineChart: Ref<BaseSerializedInfluxLineChart | null> = ref(null)
const cameraTagSelector: Ref<TagSelector<'camera'>> = ref({
  useAll: true,
  groupByValues: true,
  tag: 'camera',
  allowEmptyValues: false,
})

const compiledModel: ComputedRef<SerializedCameraBasedChart | null> = computed(() => {
  if (!props.baseSerialized || !serializedLineChart.value || !props.timeSetup) return null
  return {
    ...props.baseSerialized,
    ...serializedLineChart.value,
    cameraTagSelector: cameraTagSelector.value,
    timeSetup: props.timeSetup,
    chartType: CHART_TYPES.CAMERA_BASED_LINE,
  }
})

watch(
  model,
  () => {
    if (!model.value) return
    if (_.isEqual(model.value, compiledModel.value)) return
    cameraTagSelector.value = model.value.cameraTagSelector
    serializedLineChart.value = new InfluxLineChartBuilder({ ...model.value, chartType: CHART_TYPES.LINE }).serialize()
  },
  { deep: true, immediate: true }
)

watch(
  compiledModel,
  () => {
    if (!props.baseSerialized) return
    if (!props.timeSetup) return
    if (!serializedLineChart.value) return
    if (_.isEqual(model.value, compiledModel.value)) return
    model.value = compiledModel.value
  },
  { immediate: true, deep: true }
)
</script>

<template>
  <v-container class="ma-0 pa-0">
    <LineCreator
      v-model="serializedLineChart"
      :base-serialized="baseSerialized"
      :time-setup="timeSetup"
    />

    <v-divider
      class="my-8"
      thickness="2"
    />

    <v-layout-card
      class="pa-0"
      variant="flat"
      elevation="0"
    >
      <v-card-title class="pt-0">
        <v-icon
          icon="mdi-cctv"
          start
        />
        {{ $t('chart_sharing.creator.customize.chart_details.cameras') }}
      </v-card-title>
      <v-card-text
        class="py-0"
        :class="{ 'px-0': mobile, 'px-6': !mobile }"
      >
        <CameraTagSelector v-model="cameraTagSelector" />
      </v-card-text>
    </v-layout-card>
  </v-container>
</template>

<style scoped></style>
